import { Inject, Injectable } from "@angular/core";
import { AbstractControl, FormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { DeliveryOptions } from "app/_components/checkout-line-item-delivery/checkout-line-item-delivery.component.state";
import { CheckoutShipToService } from "app/_components/checkout-ship-to/checkout-ship-to.service";
import { CheckoutValidationService } from "app/_components/checkout-validation/checkout-validation.service";
import { InviteToPayForm } from "app/_components/invite-to-pay/invite-to-pay.model";
import { REGEX_PHONE_PATTERN } from "app/app.constants";
import { CartResult } from "entities/cart-result";
import { DeliveryTypes } from "entities/cart-search";
import { BillToInfo } from "entities/carts/bill-to-info";
import { SetDeliveryRequest } from "entities/carts/set-delivery-request";
import { UpdateCartRequest } from "entities/carts/update-cart-request";
import { addCreditCardTransactionResult } from "entities/checkout-billing-enums";
import { CountryCode } from "entities/country-code";
import { Customer } from "entities/customer";
import { DeliveryItems } from "entities/delivery-items";
import { OrderType } from "entities/order-type";
import { ShipToCustomer } from "entities/ship-to-customer";
import { StoredPaymentMethod } from "entities/stored-payment-method";
import { ToastType } from "entities/toast-type";
import { User } from "entities/user";
import { environment } from "environments/environment";
import { blankId } from "helpers/blank-id";
import {
  catchError, combineLatest, debounceTime, distinctUntilChanged,
  filter, map, of,
  switchMap, tap,
  withLatestFrom
} from "rxjs";
import { CartService } from "services/cart.service";
import { CheckoutFormService } from "services/checkout-form.service";
import { FeatureFlagService } from "services/feature-flag.service";
import { LoaderService } from "services/loader.service";
import { ToastService } from "services/toast.service";
import { AppState } from "store/app-state";
import * as BranchSelectors from 'store/branch/branch.selectors';
import * as CartActions from "store/cart/cart.actions";
import * as CartSelectors from 'store/cart/cart.selectors';
import * as ConfigurationSelectors from 'store/configuration/configuration.selectors';
import * as CustomerActions from "store/customer/customer.actions";
import * as CustomerSelectors from "store/customer/customer.selectors";
import { CustomerState } from "store/customer/customer.state";
import { i2pEmailValidator } from "validators/email.validator";



@Injectable()
export class CheckoutFormEffects {

  reactOnFormChange$ = createEffect(() => combineLatest([
    this.checkoutFormService.checkoutForm.valueChanges,
    this.store.select(CartSelectors.selectCart),
    // This is only in case the validation service resets the errors. This needs to be refactored in the future
    this.checkoutValidationService.errorsSubject
  ]).pipe(
    distinctUntilChanged(),
    filter(([_, cartData]) => (cartData?.cartId !== null || cartData?.cartId !== undefined || cartData !== undefined || cartData !== undefined )&& cartData?.cartId !== blankId),
    withLatestFrom(
      this.store.select(ConfigurationSelectors.isInternal),
      this.store.select(CartSelectors.selectIncludesProcurementFlag),
      this.store.select(ConfigurationSelectors.selectUserInfo),
      this.store.select(CartSelectors.selectIsQuote)
    ),
    tap(([[formValue, cartData], isInternal, includesProcurement, userInfo, isQuote]) => {

      this.customBehavior(cartData, null, isInternal, includesProcurement, userInfo, isQuote);

      // For debugging the checkout form
      if(!environment.production && !environment.isRunningInUnitTest){
        console.log("[Form Log] valueChange value:", formValue);
        console.log("[Form Log] Errors:",this.checkoutFormService.getAllFormErrors());
        console.log("[Form Log] Validators:",this.checkoutFormService.getAllValidators());
        console.log("[Form Log] CartData:", cartData);
        console.log("[Form Log] Form Valid:", this.checkoutFormService.checkoutForm.valid);
      }
    }),
  ), {dispatch: false});

  // Fires when user updates the checkout form
  updateDatabase$ = createEffect(() =>
    this.checkoutFormService.checkoutForm.valueChanges
    .pipe(
      filter((x) => {
          const isLineByLineSelectionPristine = this.checkoutFormService.checkoutForm.get('shippingAndDelivery.deliveryItems').pristine;
          if(!isLineByLineSelectionPristine){
            this.checkoutFormService.checkoutForm.get('shippingAndDelivery.deliveryItems').markAsPristine();
          }
          return x.shippingAndDelivery.cartData !== null &&
                 this.checkoutFormService.checkoutForm.dirty &&
                 // This prevents the 'For all items' and 'Line-by-line' selection from triggering an update
                 isLineByLineSelectionPristine;
      }),
      distinctUntilChanged(),
      debounceTime(500),
      withLatestFrom(
        this.store.select(CustomerSelectors.selectPayers),
        this.store.select(CustomerSelectors.selectShipTo),
        this.store.select(CustomerSelectors.selectStoredPaymentMethods)
      ),
      switchMap(([formGroupValue, payers, shipToCustomer, storedPaymentMethods]) => {

        const cartData: CartResult = formGroupValue.shippingAndDelivery.cartData;
        let deliveryTypes = null;
        const customerNumber = cartData.customerNumber;
        if(
          this.checkoutFormService.checkoutForm.get('shippingAndDelivery.deliveryItems').value === 'all'){
            deliveryTypes = cartData?.lineItems.map((item) => ({
              cartItemId: item.cartItemId,
              deliveryOption: item.lineId === 801 || item.lineId === 901 ? '' : cartData.deliveryType
            })).reduce((acc, item) => ({ ...acc, [item.cartItemId]: formGroupValue.shippingAndDelivery.deliveryType}), {});
          } else {
            deliveryTypes = cartData?.lineItems.map((item) => ({
              cartItemId: item.cartItemId,
              deliveryOption: item.lineId === 801 || item.lineId === 901 ? '' : DeliveryOptions.find(x => x.key === item.deliveryOption)?.option.toLowerCase()
            })).reduce((acc, item) =>
              ({ ...acc, [item.cartItemId]: item.deliveryOption })
            , {});
          }
          this.loaderService.checkoutLoaderSubject.next(true);
          const updateCartRequest = this.getUpdateCartPayload(formGroupValue, payers, shipToCustomer, storedPaymentMethods);
        return this.cartService.updateCart(updateCartRequest).pipe(
          tap((cart) => {
            this.loaderService.checkoutLoaderSubject.next(false);
            this.store.dispatch(CartActions.updateCartSuccess({cart}));
            this.checkoutFormService.checkoutForm.markAsPristine();
            this.checkoutFormService.checkoutForm.updateValueAndValidity();
          }),
          catchError((error) => {
            this.loaderService.checkoutLoaderSubject.next(false);
            this.toastService.showToast(error, ToastType.Error);
            this.store.dispatch(CartActions.updateCartFailed({error}));
            this.checkoutFormService.checkoutForm.markAsPristine();
            this.checkoutFormService.checkoutForm.updateValueAndValidity();
            return of(error);
          }),
        );
      })
  ), {dispatch: false});

  updateCartOnSubmit$ = createEffect(() => this.actions.pipe(
    ofType(CartActions.preSubmitTransaction),
    withLatestFrom(
      this.store.select(CustomerSelectors.selectPayers),
      this.store.select(CustomerSelectors.selectShipTo),
      this.store.select(CustomerSelectors.selectStoredPaymentMethods)
    ),
    switchMap(([{clearShoppingBasket, transactionType}, payers, shipToCustomer, storedPaymentMethods]) => {
      const updateCartParams = this.getUpdateCartPayload(this.checkoutFormService.checkoutForm.value, payers, shipToCustomer, storedPaymentMethods);
      return this.cartService.updateCart(updateCartParams).pipe(
        map(() => CartActions.submitTransaction({clearShoppingBasket, transactionType})),
        catchError((error) => of(CartActions.submitOrderFailed({error})))
      );
    })
  ));

  updateCheckoutForm$ = createEffect(() => combineLatest(
    [this.store.select(CartSelectors.selectCart), this.activatedRoute.queryParams, this.store.select(CustomerSelectors.selectShipTo)]
  ).pipe(
    filter(([cartData]) => cartData !== null && cartData?.cartId !== null && cartData?.cartId !== blankId),
    distinctUntilChanged(),
    withLatestFrom(
      this.store.select(CartSelectors.selectDeliverySummary),
      this.store.select(CustomerSelectors.customerState),
      this.store.select(ConfigurationSelectors.isInternal),
      this.loaderService.checkoutLoaderSubject
    ),
    tap(() => {
      // The shipto validation fails here after selecting a delivery option, not sure why yet.
      if(this.checkoutFormService.checkoutForm.dirty){
        this.checkoutFormService.checkoutForm.updateValueAndValidity();
      }
    }),
    // This Prevents the form from being overwritten as you are typing
    filter(() => this.checkoutFormService.checkoutForm.pristine),
    tap(([[cartData, queryParams, selectShipTo], deliverySummary, customer, isInternal]) =>
      this.patchForm(cartData, selectShipTo, deliverySummary.deliveryOptions, customer, queryParams, isInternal)
    ),
    map(([cartData, _]) => {
      this.store.dispatch(CartActions.cacheCart());
      return cartData;
    })
  ), {dispatch: false});

  clearQueryParametersWhenNotAddCC$ = createEffect(() => combineLatest([this.checkoutFormService.checkoutForm.valueChanges, this.activatedRoute.queryParams]).pipe(
    filter(([formValues, queryParams]) =>
      formValues.paymentDetails.billToCC !== "AddedCC" &&
      formValues.paymentDetails.addedCCResult !== null &&
      (queryParams["TRANSUCCESS"] !== null || queryParams["TRANSUCCESS"] !== undefined)),
    tap(([formValues, queryParams]) => {
      this.router.navigate(
        [],
        {
          relativeTo: this.activatedRoute,
          queryParams: {},
        }
      );
    })
  ), {dispatch: false});

  initiallySetLineByLineRadioButton$ = createEffect(() => this.store.select(CartSelectors.selectCartId).pipe(
    filter((cartId) => cartId !== blankId && cartId !== null && cartId !== undefined),
    distinctUntilChanged(),
    withLatestFrom(this.store.select(CartSelectors.selectCartLines)),
    tap(([_, lineItems]) => {
      const lineItemDeliveryOptions = lineItems?.filter(x => x.partNumber !== "FREIGHT" && x.partNumber !==  "DELIVERY:90")?.map(x => x.deliveryOption);
      const distinctlineItemDeliveryOptions = Array.from(new Set(lineItemDeliveryOptions));
      let lineByLineOption = null;

      if(distinctlineItemDeliveryOptions.length > 1){
        // Set form to line by line
        lineByLineOption = DeliveryItems.lineByLine;
      } else {
        lineByLineOption = DeliveryItems.all;
      }
      if(distinctlineItemDeliveryOptions.length === 0) {
        lineByLineOption = this.checkoutFormService.checkoutForm.value.shippingAndDelivery.deliveryItems;
      }

      const lineByLineOptionControl = this.checkoutFormService.checkoutForm.get('shippingAndDelivery.deliveryItems');
      lineByLineOptionControl.patchValue(lineByLineOption);
    })
  ), {dispatch: false});

  shipToValidation$ = createEffect(() => combineLatest([
    this.store.select(CustomerSelectors.selectShipTo),
    this.store.select(CartSelectors.deliveryRequiresAddress),
    this.store.select(BranchSelectors.selectedBranchCountryCode),
    this.checkoutShipToService.editing$
  ]).pipe(
    tap(([shipTo, deliveryRequiresAddress, countryCode, editingActive]) => {
      let form = null;
      if (editingActive) {
        form = this.checkoutShipToService.getForm();
      }
      if (!editingActive && shipTo) {
        form = this.checkoutShipToService.initForm(shipTo, deliveryRequiresAddress, countryCode === CountryCode.CA);
      }
      this.checkoutFormService.setControl("selectedShipTo", form, editingActive);
    }
  )), {dispatch: false});

  disableDeliveryOptionsWhenFreightLoading$ = createEffect(() => this.store.select(CartSelectors.selectIsShippingRatesLoading).pipe(
    tap((isLoading) => {
      const deliveryTypeControl = this.checkoutFormService.checkoutForm.controls.shippingAndDelivery.controls.deliveryType;
      if(isLoading){
        deliveryTypeControl.disable({emitEvent: false});
      } else {
        deliveryTypeControl.enable({emitEvent: false});
      }
    })
  ), {dispatch: false});

  constructor(
    @Inject(REGEX_PHONE_PATTERN) public regexPhonePattern: string,
    private cartService: CartService,
    private toastService: ToastService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private checkoutValidationService: CheckoutValidationService,
    private checkoutFormService: CheckoutFormService,
    private store: Store<AppState>,
    private loaderService: LoaderService,
    private featureFlagService: FeatureFlagService,
    private checkoutShipToService: CheckoutShipToService,
    private actions: Actions
  ) { }

  customBehavior(cartData: CartResult, shipToCustomer: ShipToCustomer, isInternal: boolean, includesProcurement: boolean, user: User, isQuote: boolean){
    const altEmailFormControl = this.checkoutFormService.checkoutForm.get('additionalDetails.altEmail');
    const transactionTypeControl = this.checkoutFormService.checkoutForm.get('checkoutDetails.transactionType');
    const orderChannelControl = this.checkoutFormService.checkoutForm.get('checkoutDetails.orderChannel');
    const billToCCControl = this.checkoutFormService.checkoutForm.get('paymentDetails.billToCC');
    const i2pEmailControl = this.checkoutFormService.checkoutForm.get('paymentDetails.i2pEmail');
    const i2pSmsControl = this.checkoutFormService.checkoutForm.get('paymentDetails.i2pSms');
    const optInControl = this.checkoutFormService.checkoutForm.get('smsOpt.optIn');
    const optInContactNameControl = this.checkoutFormService.checkoutForm.get('smsOpt.contactName');
    const optInMobileNumberControl = this.checkoutFormService.checkoutForm.get('smsOpt.mobileNumber');
    const specialInstructionControl = this.checkoutFormService.checkoutForm.get('additionalDetails.specialInstructions');

    if(transactionTypeControl.value == OrderType.Quote){
      orderChannelControl.removeValidators(Validators.required);
    } else {
      orderChannelControl.addValidators(Validators.required);
    }
    orderChannelControl.updateValueAndValidity({emitEvent: false});

    // Email required validation
    const needsProcurement = cartData.lineItems
      .filter(x => x.partNumber !== "FREIGHT" && x.partNumber !== "DELIVERY:90")
      .some(x => x.quantityAvailable === 0);

    const emailIsRequired = (isInternal && (
      (cartData.transactionType === OrderType.Quote && !isQuote) || (!needsProcurement && !includesProcurement)
      ? false
      : (includesProcurement || cartData.deliveryType === 'shipto')
    ));

    if (emailIsRequired) {
      altEmailFormControl.addValidators(Validators.required);
    } else {
      altEmailFormControl.removeValidators(Validators.required);
    }
    altEmailFormControl.updateValueAndValidity({emitEvent: false});


    if(
      !!this.checkoutFormService.checkoutForm.value?.shippingAndDelivery?.cartData?.orderNumber &&
      (this.checkoutFormService.checkoutForm.value?.shippingAndDelivery?.cartData?.isLocked &&
      this.checkoutFormService.checkoutForm.value?.shippingAndDelivery?.cartData?.updatedById !== user?.id)
      ){
      specialInstructionControl.disable({emitEvent: false});
    } else {
      specialInstructionControl.enable({emitEvent: false});
    }

    if(cartData.paymentSelection === "CCI2P"){
      i2pEmailControl.addValidators(i2pEmailValidator);
      i2pSmsControl.addValidators(Validators.pattern(this.regexPhonePattern));
    } else {
      i2pEmailControl.removeValidators(i2pEmailValidator);
      i2pSmsControl.removeValidators(Validators.pattern(this.regexPhonePattern));
    }

    i2pEmailControl.updateValueAndValidity({emitEvent: false});
    i2pSmsControl.updateValueAndValidity({emitEvent: false});

    if(!isInternal){
      orderChannelControl.removeValidators(Validators.required);
    }
    orderChannelControl.updateValueAndValidity({emitEvent: false});

    if(optInControl.value){
      optInContactNameControl.enable({emitEvent: false});
      optInMobileNumberControl.enable({emitEvent: false});
    } else {
      optInContactNameControl.disable({emitEvent: false});
      optInMobileNumberControl.disable({emitEvent: false});
    }

    if(this.checkoutFormService.checkoutForm.value.paymentDetails.billTo !== "CASH-CCOnline"){
      billToCCControl.disable({emitEvent: false});
    } else {
      billToCCControl.enable({emitEvent: false});
    }
  }

  patchForm(cartData: CartResult, selectShipTo: ShipToCustomer, deliveryOptions: string[], customer: CustomerState, paymentQueryParams: Params, isUserInternal: boolean): void {
    let poNumber = '';
    if(cartData.poNumber === null){
      poNumber = customer.selectedCustomer.customerPONumber;
    } else {
      poNumber = cartData.poNumber;
    }
    let addedCCResult: string;

    switch(paymentQueryParams['TRANSUCCESS']){
      case 'TRUE':
        addedCCResult = addCreditCardTransactionResult.Added;
        break;
      case 'FALSE':
        addedCCResult = addCreditCardTransactionResult.Failed;
        break;
      default:
        addedCCResult = null;
        break;
    }

    let i2p = null;
    if(cartData.i2pEmail !== null && cartData.i2pEmail !== undefined && cartData.i2pEmail !== ""){
      i2p = "email";
    }
    if(cartData.i2pSmsNumber !== null && cartData.i2pSmsNumber !== undefined && cartData.i2pSmsNumber !== ""){
      i2p = "sms";
    }
    if(i2p === null && cartData.paymentSelection === 'CCI2P'){
      i2p = this.checkoutFormService.checkoutForm.value.paymentDetails.i2p;
    }

    this.checkoutFormService.checkoutForm.patchValue({
      checkoutDetails: {
        poNumber,
        unitNumber: cartData.unitNumber,
        transactionType: cartData.transactionType,
        orderChannel: cartData.orderChannel
      },
      shippingAndDelivery: {
        deliveryType: cartData.deliveryType?.toLowerCase(),
        cartData: { ...cartData, isUserInternal },
        shipTo: {
          shipToAccount: selectShipTo,
          isEdited: cartData.isAlternateAddressEdited
        }
      },
      paymentDetails: {
        selectedPayment: cartData.paymentSelection,
        billTo: cartData.billToAddressNo,
        billToCC: cartData.paymentSelection,
        i2p,
        i2pEmail: cartData.i2pEmail,
        i2pSms: cartData.i2pSmsNumber,
        addedCCResult
      },
      additionalDetails: {
        altEmail: cartData.alternateEmail === null ? "" : cartData.alternateEmail,
        specialInstructions: cartData.specialInstruction
      },
      smsOpt: {
        optIn: cartData?.smsOptIn,
        contactName: cartData?.smsContactName,
        mobileNumber: cartData?.smsMobileNumber
      }
    }, {emitEvent: true, onlySelf: true});

    if (selectShipTo && !customer.shipToEditing && !this.checkoutFormService.checkoutForm.pristine ) {
      this.store.dispatch(CustomerActions.setShipToAddress({
        shipToAddress: selectShipTo
      }));
    }

    if (!cartData.smsOptIn) {
      this.checkoutFormService.checkoutForm['controls'].smsOpt['controls'].contactName.disable({emitEvent: false});
      this.checkoutFormService.checkoutForm['controls'].smsOpt['controls'].mobileNumber.disable({emitEvent: false});
    }
    this.checkoutValidationService.validate(this.checkoutFormService.checkoutForm);
    this.checkoutFormService.checkoutForm.updateValueAndValidity({emitEvent: true});
    this.checkoutFormService.checkoutForm.markAsPristine();
  }

  getAllFormErrors(formGroup: FormGroup): { [key: string]: any } {
    const errors: { [key: string]: any } = {};

    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      if (control instanceof FormGroup) {
        const subErrors = this.getAllFormErrors(control);
        if (Object.keys(subErrors).length > 0) {
          errors[key] = subErrors;
        }
      } else {
        if (control && control.errors != null) {
          errors[key] = control.errors;
        }
      }
    });

    return errors;
  }

  getDeliveryItemsTab(cartData: CartResult, deliveryOptions: string[], isInternalUser: boolean): string {
    let tab = (cartData.deliveryType || !deliveryOptions.length) ? DeliveryItems.all : DeliveryItems.lineByLine;
    const deliveryItems = this.deliveryItems();
    if (deliveryItems !== DeliveryItems.all) {
      tab = deliveryItems;
    }
    if (!isInternalUser) {
      tab = DeliveryItems.all;
    }
    return tab;
  }
  deliveryItems(): DeliveryItems | undefined {
    const x = this.checkoutFormService.checkoutForm.get('shippingAndDelivery')?.get('deliveryItems')?.value;
    return x;
  }

  getPaymentTokenFromQueryParams(queryParams: Params){
    const newStoredPaymentMethod: StoredPaymentMethod = {
      id: "",
      paymentProviderName: "",
      paymentToken: queryParams['REPTOKEN'],
      processorToken: queryParams['TRANSARMORTOKEN'],
      customerId: "",
      driversLicenseNumber: "",
      driversLicenseState: "",
      creditCardAccountExpiration: queryParams['CCEXP'],
      creditCardAccountName: queryParams['REPCCNAME'],
      creditCardNickname: "",
      creditCardAccountNumber: queryParams['CCACCOUNT'],
      tokenBrand: "",
      creditCardType: queryParams['CCCARDTYPE'],
      checkingAccountName: "",
      checkingAccountRoutingNumber: "",
      checkingAccountNumber: "",
      isCheckingAccountBusiness: "",
      isCheckingAccountSavings: ""
    };
    return newStoredPaymentMethod;
  }

  getUpdateCartPayload(formGroupValue: any, payers: Customer[], shipToCustomer: ShipToCustomer, storedPaymentMethods: StoredPaymentMethod[]) {
    const cartData: CartResult = formGroupValue.shippingAndDelivery.cartData;
      let deliveryTypes = null;
      const customerNumber = cartData.customerNumber;
      if(
        this.checkoutFormService.checkoutForm.get('shippingAndDelivery.deliveryItems').value === 'all'){
          deliveryTypes = cartData?.lineItems.map((item) => ({
            cartItemId: item.cartItemId,
            deliveryOption: item.lineId === 801 || item.lineId === 901 ? '' : cartData.deliveryType
          })).reduce((acc, item) => ({ ...acc, [item.cartItemId]: formGroupValue.shippingAndDelivery.deliveryType}), {});
        } else {
          deliveryTypes = cartData?.lineItems.map((item) => ({
            cartItemId: item.cartItemId,
            deliveryOption: item.lineId === 801 || item.lineId === 901 ? '' : DeliveryOptions.find(x => x.key === item.deliveryOption)?.option.toLowerCase()
          })).reduce((acc, item) =>
            ({ ...acc, [item.cartItemId]: item.deliveryOption })
          , {});
        }
      const setDeliveryRequest: SetDeliveryRequest = {
        addDeliveryFees: true,
        branchCode: cartData.branchCode,
        cartId: cartData.cartId,
        headerDeliveryType: formGroupValue.shippingAndDelivery.deliveryType as DeliveryTypes,
        customerNumber,
        deliveryTypes
      };

      const billToCustomerNumber = formGroupValue.paymentDetails.billTo;
      const billTocustomer: Customer = payers.find(payer => payer.addressNumber === billToCustomerNumber);
      const updateBillToRequest: BillToInfo = billToCustomerNumber === null? null : {
        addressNumber: billToCustomerNumber,
        customerName: billTocustomer.customerName
      };
      const cartShipTo = formGroupValue?.shippingAndDelivery?.shipTo?.shipToAccount;
      let updateShipToRequest: ShipToCustomer = {...cartShipTo};
      if(cartShipTo !== null && (cartData.deliveryType === null || cartData.deliveryType === '') ){
       updateShipToRequest = null;
       this.checkoutFormService.checkoutForm.controls.shippingAndDelivery.controls.shipTo.controls.shipToAccount.patchValue(null, {emitEvent: false});
      }
      if(!cartShipTo && !!shipToCustomer && (setDeliveryRequest.headerDeliveryType !== null && setDeliveryRequest.headerDeliveryType !== undefined)) {
        updateShipToRequest = {...shipToCustomer};
      }

      const storedPaymentMethod = storedPaymentMethods.find(
        (paymentMethod: StoredPaymentMethod) => paymentMethod.id === formGroupValue.paymentDetails.billToCC);


      let i2pEmail = formGroupValue.paymentDetails.i2pEmail;
      let i2pSmsNumber = formGroupValue.paymentDetails.i2pSms;
      if((formGroupValue.paymentDetails.i2p === "email" &&
         formGroupValue.paymentDetails.i2pSms !== null && formGroupValue.paymentDetails.i2pSms !== "") ||
         (formGroupValue.paymentDetails.i2p === "sms" &&
         formGroupValue.paymentDetails.i2pEmail !== null && formGroupValue.paymentDetails.i2pEmail !== "")
      ){
        i2pEmail = null;
        i2pSmsNumber = null;
      }
      const hasFrontCounterSelfPickOrBasketReturnItems =
      (setDeliveryRequest.headerDeliveryType === 'adhoc' || setDeliveryRequest.headerDeliveryType === 'selfpick') ||
      Object.values(setDeliveryRequest.deliveryTypes).some(x => x === 'adhoc' || x === 'selfpick');

      if(hasFrontCounterSelfPickOrBasketReturnItems){
        i2pEmail = null;
        i2pSmsNumber = null;
      }

      const updateCartRequest = {
        alternateEmail: formGroupValue.additionalDetails.altEmail,
        cartId: formGroupValue.shippingAndDelivery.cartData?.cartId,
        poNumber: formGroupValue.checkoutDetails?.poNumber?.toUpperCase(),
        specialInstruction: formGroupValue.additionalDetails.specialInstructions,
        unitNumber: formGroupValue.checkoutDetails?.unitNumber,
        setDeliveryRequest,
        updateBillToRequest,
        updateShipToRequest,
        transactionType: formGroupValue.checkoutDetails.transactionType?.toString(),
        orderChannel: formGroupValue.checkoutDetails.orderChannel?.toString(),
        i2pEmail,
        i2pSmsNumber,
        paymentMethod: formGroupValue.paymentDetails.billToCC,
        storedToken: storedPaymentMethod ?? null,
        contactName: formGroupValue.smsOpt?.optIn? formGroupValue.smsOpt?.contactName: null,
        mobileNumber: formGroupValue.smsOpt?.optIn? formGroupValue.smsOpt?.mobileNumber: null,
        optIn: formGroupValue.smsOpt?.optIn
      } as UpdateCartRequest;

      if( (updateBillToRequest?.addressNumber !== "CASH-CCOnline" &&
           updateCartRequest.paymentMethod !== null ) ||
           updateCartRequest.transactionType === OrderType.Quote){
        updateCartRequest.paymentMethod = null;
        updateCartRequest.storedToken = null;
        updateCartRequest.i2pEmail = null;
        updateCartRequest.i2pSmsNumber = null;
      }

      if (
        this.checkoutFormService.checkoutForm.get('paymentDetails.billToCC').value !== null &&
        this.checkoutFormService.checkoutForm.get('paymentDetails.billToCC').value !== '' &&
        updateBillToRequest?.addressNumber !== "CASH-CCOnline"
        ) {
          this.checkoutFormService.checkoutForm.get('paymentDetails.billToCC').setValue('');
          this.checkoutFormService.checkoutForm.markAsPristine();
      }

      if (
        this.checkoutFormService.checkoutForm.get('paymentDetails.billToCC').value !== null &&
        this.checkoutFormService.checkoutForm.get('paymentDetails.billToCC').value !== '' &&
        updateBillToRequest?.addressNumber === "CASH-CCOnline"
        ) {
          this.checkoutFormService.checkoutForm.markAsPristine();
      }

      return updateCartRequest;
  }

  setCCValidators() {
    const phoneNumberValidation = [ Validators.required,
      Validators.pattern(this.regexPhonePattern)
    ].filter((v) => v);
    const addedCCPattern = "(^$)|(^Added$)";

    const payment: string = this.checkoutFormService.checkoutForm.get('selectedPayment').value;
    const i2pFormControl: AbstractControl<InviteToPayForm> = this.checkoutFormService.checkoutForm.get('inviteToPayForm');
    const addedCCResultControl: AbstractControl<string> = this.checkoutFormService.checkoutForm.get('addedCCResult');
    const billToCC = this.checkoutFormService.checkoutForm.get('billToCC');

    switch (payment) {
      case 'StoredCC': {
        // clear InviteToPay
        i2pFormControl.get('i2p').clearValidators();
        i2pFormControl.get('i2p').updateValueAndValidity();
        i2pFormControl.get('i2pEmail').clearValidators();
        i2pFormControl.get('i2pEmail').updateValueAndValidity();
        i2pFormControl.get('i2pSms').clearValidators();
        i2pFormControl.get('i2pSms').updateValueAndValidity();
        // clear addedCCResult
        addedCCResultControl.clearValidators();
        addedCCResultControl.updateValueAndValidity();

        billToCC.clearValidators();
        billToCC.updateValueAndValidity();
        break;
      }
      case 'AddedCC': {
        // clear InviteToPay
        i2pFormControl.get('i2p').clearValidators();
        i2pFormControl.get('i2p').updateValueAndValidity();
        i2pFormControl.get('i2pEmail').clearValidators();
        i2pFormControl.get('i2pEmail').updateValueAndValidity();
        i2pFormControl.get('i2pSms').clearValidators();
        i2pFormControl.get('i2pSms').updateValueAndValidity();
        addedCCResultControl.clearValidators();
        addedCCResultControl.setValidators([ Validators.required, Validators.pattern( addedCCPattern )] );
        addedCCResultControl.updateValueAndValidity();

        billToCC.clearValidators();
        billToCC.updateValueAndValidity();
        break;
      }
      case 'CCI2P': {
        i2pFormControl.get('i2p').setValidators([Validators.required]);
        i2pFormControl.get('i2p').updateValueAndValidity();

        if (i2pFormControl.get('i2p').valid) {
          // if (this.checkoutFormService.checkoutForm.value.paymentDetails.inviteToPayForm. === i2pType.Email) {
          //   i2pFormControl.get('i2pEmail').setValidators([Validators.required, isValidEmail]);
          //   i2pFormControl.get('i2pEmail').updateValueAndValidity();
          //   i2pFormControl.get('i2pSms').clearValidators();
          //   i2pFormControl.get('i2pSms').updateValueAndValidity();
          // } else {
          //   i2pFormControl.get('i2pSms').setValidators(phoneNumberValidation);
          //   i2pFormControl.get('i2pSms').updateValueAndValidity();
          //   i2pFormControl.get('i2pEmail').clearValidators();
          //   i2pFormControl.get('i2pEmail').updateValueAndValidity();
          // }
          // clear addedCCResult
          addedCCResultControl.clearValidators();
          addedCCResultControl.updateValueAndValidity();

          billToCC.clearValidators();
          billToCC.updateValueAndValidity();
        }
        break;
      }
      default: {
        // clear InviteToPay
        i2pFormControl.get('i2p').clearValidators();
        i2pFormControl.get('i2p').updateValueAndValidity();
        i2pFormControl.get('i2pEmail').clearValidators();
        i2pFormControl.get('i2pEmail').updateValueAndValidity();
        i2pFormControl.get('i2pSms').clearValidators();
        i2pFormControl.get('i2pSms').updateValueAndValidity();
        // clear addedCCResult
        addedCCResultControl.clearValidators();
        addedCCResultControl.updateValueAndValidity();

        const billToValue = this.checkoutFormService.checkoutForm.get('billTo').value;
        const billToCCValue = this.checkoutFormService.checkoutForm.get('billToCC').value;

        // if (billToCCValue === null && billToValue === billToType.CASH_CCOnline && this.transactionType == OrderType.Order) {
        //   billToCC.setValidators([ Validators.required]);
        //   billToCC.updateValueAndValidity();
        // } else {
        //   billToCC.clearValidators();
        //   billToCC.updateValueAndValidity();
        // }
          break;
      }

    }
  }
}

